import { createContext, useContext, useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/config";

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const [alphabets, setAlphabets] = useState([]);
  const [words, setWords] = useState([]);
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize default data
  const initializeDefaultData = async () => {
    try {
      // Default alphabets data
      const defaultAlphabets = [
        {
          id: "hiragana",
          name: "Hiragana (ひらがな)",
          description:
            "The basic Japanese alphabet used for native Japanese words",
          characters: [
            { char: "あ", romaji: "a", audio: "" },
            { char: "い", romaji: "i", audio: "" },
            { char: "う", romaji: "u", audio: "" },
            { char: "え", romaji: "e", audio: "" },
            { char: "お", romaji: "o", audio: "" },
          ],
        },
        {
          id: "katakana",
          name: "Katakana (カタカナ)",
          description: "Used for foreign words and emphasis",
          characters: [
            { char: "ア", romaji: "a", audio: "" },
            { char: "イ", romaji: "i", audio: "" },
            { char: "ウ", romaji: "u", audio: "" },
            { char: "エ", romaji: "e", audio: "" },
            { char: "オ", romaji: "o", audio: "" },
          ],
        },
        {
          id: "kanji",
          name: "Kanji (漢字)",
          description: "Chinese characters used in Japanese writing",
          characters: [
            { char: "日", romaji: "hi/nichi", audio: "" },
            { char: "本", romaji: "hon/moto", audio: "" },
            { char: "人", romaji: "hito/jin", audio: "" },
            { char: "大", romaji: "oo/dai", audio: "" },
            { char: "小", romaji: "ko/shou", audio: "" },
          ],
        },
      ];

      // Default words data
      const defaultWords = [
        {
          id: "greetings",
          category: "Basic Greetings",
          items: [
            {
              japanese: "こんにちは",
              romaji: "Konnichiwa",
              english: "Hello",
              audio: "",
            },
            {
              japanese: "ありがとう",
              romaji: "Arigatou",
              english: "Thank you",
              audio: "",
            },
            {
              japanese: "おはよう",
              romaji: "Ohayou",
              english: "Good morning",
              audio: "",
            },
          ],
        },
        {
          id: "numbers",
          category: "Numbers",
          items: [
            { japanese: "一", romaji: "Ichi", english: "One", audio: "" },
            { japanese: "二", romaji: "Ni", english: "Two", audio: "" },
            { japanese: "三", romaji: "San", english: "Three", audio: "" },
          ],
        },
        {
          id: "colors",
          category: "Colors",
          items: [
            { japanese: "赤", romaji: "Aka", english: "Red", audio: "" },
            { japanese: "青", romaji: "Ao", english: "Blue", audio: "" },
            { japanese: "緑", romaji: "Midori", english: "Green", audio: "" },
          ],
        },
      ];

      // Default sentences data
      const defaultSentences = [
        {
          id: "basic_conversations",
          category: "Basic Conversations",
          items: [
            {
              japanese: "お名前は何ですか？",
              romaji: "Onamae wa nan desu ka?",
              english: "What is your name?",
              audio: "",
            },
            {
              japanese: "よろしくお願いします。",
              romaji: "Yoroshiku onegaishimasu.",
              english: "Nice to meet you.",
              audio: "",
            },
            {
              japanese: "どこから来ましたか？",
              romaji: "Doko kara kimashita ka?",
              english: "Where are you from?",
              audio: "",
            },
          ],
        },
        {
          id: "daily_expressions",
          category: "Daily Expressions",
          items: [
            {
              japanese: "お疲れ様でした。",
              romaji: "Otsukaresama deshita.",
              english: "Good work today.",
              audio: "",
            },
            {
              japanese: "いただきます。",
              romaji: "Itadakimasu.",
              english: "Let's eat.",
              audio: "",
            },
            {
              japanese: "おやすみなさい。",
              romaji: "Oyasuminasai.",
              english: "Good night.",
              audio: "",
            },
          ],
        },
        {
          id: "questions_answers",
          category: "Questions & Answers",
          items: [
            {
              japanese: "何時ですか？",
              romaji: "Nanji desu ka?",
              english: "What time is it?",
              audio: "",
            },
            {
              japanese: "いくらですか？",
              romaji: "Ikura desu ka?",
              english: "How much is it?",
              audio: "",
            },
            {
              japanese: "どこですか？",
              romaji: "Doko desu ka?",
              english: "Where is it?",
              audio: "",
            },
          ],
        },
      ];

      // Check if data exists, if not, initialize with default data
      const alphabetsSnapshot = await getDocs(collection(db, "alphabets"));
      if (alphabetsSnapshot.empty) {
        for (const alphabet of defaultAlphabets) {
          await setDoc(doc(db, "alphabets", alphabet.id), alphabet);
        }
      }

      const wordsSnapshot = await getDocs(collection(db, "words"));
      if (wordsSnapshot.empty) {
        for (const wordCategory of defaultWords) {
          await setDoc(doc(db, "words", wordCategory.id), wordCategory);
        }
      }

      const sentencesSnapshot = await getDocs(collection(db, "sentences"));
      if (sentencesSnapshot.empty) {
        for (const sentenceCategory of defaultSentences) {
          await setDoc(
            doc(db, "sentences", sentenceCategory.id),
            sentenceCategory
          );
        }
      }
    } catch (error) {
      console.error("Error initializing default data:", error);
    }
  };

  // Load data from Firestore
  useEffect(() => {
    const loadData = async () => {
      try {
        await initializeDefaultData();

        // Set up real-time listeners
        const unsubscribeAlphabets = onSnapshot(
          collection(db, "alphabets"),
          (snapshot) => {
            const alphabetsData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setAlphabets(alphabetsData);
          }
        );

        const unsubscribeWords = onSnapshot(
          collection(db, "words"),
          (snapshot) => {
            const wordsData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setWords(wordsData);
          }
        );

        const unsubscribeSentences = onSnapshot(
          collection(db, "sentences"),
          (snapshot) => {
            const sentencesData = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setSentences(sentencesData);
          }
        );

        setLoading(false);

        return () => {
          unsubscribeAlphabets();
          unsubscribeWords();
          unsubscribeSentences();
        };
      } catch (error) {
        console.error("Error loading data:", error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // CRUD operations
  const updateAlphabet = async (id, data) => {
    try {
      await setDoc(doc(db, "alphabets", id), data);
    } catch (error) {
      console.error("Error updating alphabet:", error);
      throw error;
    }
  };

  const updateWordCategory = async (id, data) => {
    try {
      await setDoc(doc(db, "words", id), data);
    } catch (error) {
      console.error("Error updating word category:", error);
      throw error;
    }
  };

  const updateSentenceCategory = async (id, data) => {
    try {
      await setDoc(doc(db, "sentences", id), data);
    } catch (error) {
      console.error("Error updating sentence category:", error);
      throw error;
    }
  };

  const addWordCategory = async (data) => {
    try {
      const docRef = await addDoc(collection(db, "words"), data);
      return docRef.id;
    } catch (error) {
      console.error("Error adding word category:", error);
      throw error;
    }
  };

  const addSentenceCategory = async (data) => {
    try {
      const docRef = await addDoc(collection(db, "sentences"), data);
      return docRef.id;
    } catch (error) {
      console.error("Error adding sentence category:", error);
      throw error;
    }
  };

  const deleteWordCategory = async (id) => {
    try {
      await deleteDoc(doc(db, "words", id));
    } catch (error) {
      console.error("Error deleting word category:", error);
      throw error;
    }
  };

  const deleteSentenceCategory = async (id) => {
    try {
      await deleteDoc(doc(db, "sentences", id));
    } catch (error) {
      console.error("Error deleting sentence category:", error);
      throw error;
    }
  };

  const value = {
    alphabets,
    words,
    sentences,
    loading,
    updateAlphabet,
    updateWordCategory,
    updateSentenceCategory,
    addWordCategory,
    addSentenceCategory,
    deleteWordCategory,
    deleteSentenceCategory,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
